'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import WhatsAppFab from '@/app/components/WhatsAppFab';
import { Search, MapPin, Calendar, Clock, Car, CreditCard, CheckCircle2, XCircle, AlertCircle, Loader2, Phone } from 'lucide-react';

type BookingStatus = 'Payment Pending' | 'Advance Paid' | 'Confirmed' | 'Driver Assigned' | 'Trip Started' | 'Completed' | 'Cancelled';

interface BookingData {
  bookingId: string;
  name: string;
  mobile: string;
  pickup: string;
  drop: string;
  date: string;
  time: string;
  vehicle: string;
  totalAmount: number;
  amountPaid: number;
  pendingAmount: number;
  status: string;
  paymentStatus: string;
  createdAt: string;
}

const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: React.ReactNode; step: number }> = {
  'Payment Pending':  { color: 'text-amber-700', bg: 'bg-amber-100', icon: <AlertCircle size={16} />, step: 0 },
  'Advance Paid':     { color: 'text-blue-700',  bg: 'bg-blue-100',  icon: <CreditCard size={16} />, step: 1 },
  'Confirmed':        { color: 'text-secondary',  bg: 'bg-secondary/10', icon: <CheckCircle2 size={16} />, step: 2 },
  'Driver Assigned':  { color: 'text-primary',   bg: 'bg-primary-light', icon: <Car size={16} />, step: 3 },
  'Trip Started':     { color: 'text-purple-700', bg: 'bg-purple-100', icon: <MapPin size={16} />, step: 4 },
  'Completed':        { color: 'text-secondary',  bg: 'bg-secondary/10', icon: <CheckCircle2 size={16} />, step: 5 },
  'Cancelled':        { color: 'text-red-700',   bg: 'bg-red-100',   icon: <XCircle size={16} />, step: -1 },
};

const STATUS_STEPS: BookingStatus[] = [
  'Payment Pending', 'Advance Paid', 'Confirmed', 'Driver Assigned', 'Trip Started', 'Completed'
];

export default function BookingStatusPage() {
  const [searchType, setSearchType] = useState<'id' | 'mobile'>('id');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<BookingData[]>([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSearch = () => {
    if (!query.trim()) return;
    setLoading(true);
    setTimeout(() => {
      const bookings: BookingData[] = JSON.parse(localStorage.getItem('ghumoji_bookings') || '[]');
      let found: BookingData[] = [];
      if (searchType === 'id') {
        found = bookings.filter((b) => b.bookingId.toLowerCase() === query.trim().toLowerCase());
      } else {
        found = bookings.filter((b) => b.mobile === query.trim().replace(/\s/g, ''));
      }
      setResults(found);
      setSearched(true);
      setLoading(false);
    }, 800);
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-muted/30 pt-20 pb-16">
        {/* Page Header */}
        <div className="bg-primary py-10">
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
            <h1 className="text-3xl font-bold text-white mb-1">Track Your Booking</h1>
            <p className="text-white/75 text-base">
              Enter your Booking ID or mobile number to check your trip status
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-10">
          {/* Search Card */}
          <div className="bg-white rounded-2xl border border-border shadow-sm p-6 mb-8">
            <h2 className="font-bold text-foreground text-lg mb-5 flex items-center gap-2">
              <Search size={18} className="text-primary" />
              Search Booking
            </h2>

            <div className="flex gap-3 mb-5">
              <button
                type="button"
                onClick={() => setSearchType('id')}
                className={`flex-1 py-2.5 rounded-xl font-semibold text-sm border-2 transition-all ${
                  searchType === 'id' ? 'border-primary bg-primary text-white' : 'border-border text-muted-foreground hover:border-primary'
                }`}
              >
                By Booking ID
              </button>
              <button
                type="button"
                onClick={() => setSearchType('mobile')}
                className={`flex-1 py-2.5 rounded-xl font-semibold text-sm border-2 transition-all ${
                  searchType === 'mobile' ? 'border-primary bg-primary text-white' : 'border-border text-muted-foreground hover:border-primary'
                }`}
              >
                By Mobile Number
              </button>
            </div>

            <div className="flex gap-3">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={searchType === 'id' ? 'e.g. GJT-ABC123-XY12' : 'e.g. 9876543210'}
                className="input-field flex-1"
              />
              <button
                onClick={handleSearch}
                disabled={loading || !query.trim()}
                className="btn-accent px-6 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              </button>
            </div>
          </div>

          {/* Results */}
          {searched && results.length === 0 && (
            <div className="bg-white rounded-2xl border border-border shadow-sm p-8 text-center">
              <XCircle size={40} className="text-muted-foreground mx-auto mb-3" />
              <p className="font-bold text-foreground mb-1">No Booking Found</p>
              <p className="text-sm text-muted-foreground mb-4">
                We couldn&apos;t find any booking with that {searchType === 'id' ? 'Booking ID' : 'mobile number'}.
              </p>
              <a
                href="tel:+918894915859"
                className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:underline"
              >
                <Phone size={15} />
                Call us: +91 88949 15859
              </a>
            </div>
          )}

          {results.map((booking) => {
            const statusCfg = STATUS_CONFIG[booking.status] || STATUS_CONFIG['Confirmed'];
            const currentStep = statusCfg.step;

            return (
              <div key={booking.bookingId} className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden mb-5">
                {/* Header */}
                <div className="bg-primary-light px-6 py-4 flex items-center justify-between border-b border-border">
                  <div>
                    <p className="text-xs text-muted-foreground">Booking ID</p>
                    <p className="font-extrabold text-primary text-lg font-mono">{booking.bookingId}</p>
                  </div>
                  <span className={`flex items-center gap-1.5 font-semibold text-sm px-3 py-1.5 rounded-full ${statusCfg.bg} ${statusCfg.color}`}>
                    {statusCfg.icon}
                    {booking.status}
                  </span>
                </div>

                {/* Status Timeline */}
                {booking.status !== 'Cancelled' && (
                  <div className="px-6 py-5 border-b border-border">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">Booking Progress</p>
                    <div className="flex items-center gap-0">
                      {STATUS_STEPS.map((step, idx) => {
                        const isCompleted = currentStep > idx;
                        const isCurrent = currentStep === idx;
                        return (
                          <React.Fragment key={step}>
                            <div className="flex flex-col items-center flex-shrink-0">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                                isCompleted ? 'bg-secondary border-secondary text-white' : isCurrent ?'bg-primary border-primary text-white': 'bg-white border-border text-muted-foreground'
                              }`}>
                                {isCompleted ? '✓' : idx + 1}
                              </div>
                              <p className={`text-[9px] mt-1 text-center w-14 leading-tight ${
                                isCurrent ? 'text-primary font-bold' : isCompleted ? 'text-secondary' : 'text-muted-foreground'
                              }`}>{step}</p>
                            </div>
                            {idx < STATUS_STEPS.length - 1 && (
                              <div className={`flex-1 h-0.5 mb-4 ${isCompleted ? 'bg-secondary' : 'bg-border'}`} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Trip Details */}
                <div className="p-6 grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-2">
                    <MapPin size={15} className="text-secondary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Pickup</p>
                      <p className="text-sm font-semibold text-foreground">{booking.pickup}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin size={15} className="text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Drop</p>
                      <p className="text-sm font-semibold text-foreground">{booking.drop}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar size={15} className="text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Date</p>
                      <p className="text-sm font-semibold text-foreground">{booking.date}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Clock size={15} className="text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Time</p>
                      <p className="text-sm font-semibold text-foreground">{booking.time}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Car size={15} className="text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Vehicle</p>
                      <p className="text-sm font-semibold text-foreground">{booking.vehicle}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CreditCard size={15} className="text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Payment</p>
                      <p className="text-sm font-semibold text-foreground">{booking.paymentStatus}</p>
                      {booking.pendingAmount > 0 && (
                        <p className="text-xs text-amber-600">Due: ₹{booking.pendingAmount.toLocaleString('en-IN')}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="px-6 pb-5 flex gap-3">
                  <a
                    href={`https://wa.me/918894915859?text=Hi%20Ghumo%20Ji%20Travels%2C%20my%20Booking%20ID%20is%20${booking.bookingId}.%20Please%20update%20me%20on%20my%20trip%20status.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-[#25D366] text-white font-semibold px-4 py-2.5 rounded-xl hover:bg-[#20b858] transition-colors text-sm"
                  >
                    <Phone size={14} />
                    WhatsApp Support
                  </a>
                  <a
                    href="tel:+918894915859"
                    className="flex items-center gap-2 border-2 border-primary text-primary font-semibold px-4 py-2.5 rounded-xl hover:bg-primary-light transition-colors text-sm"
                  >
                    <Phone size={14} />
                    Call Us
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </main>
      <Footer />
      <WhatsAppFab />
    </>
  );
}
